// ./dialogs/EditTransactionDialog.jsx
import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Divider,
  Card,
  CardContent
} from '@mui/material';
import MobileDialog from './MobileDialog';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { he } from 'date-fns/locale';
import { format } from 'date-fns';
import API from '../../services/api';
import PaymentMethodRows from '../components/PaymentMethodRows';

// Protected subtypes that cannot be edited (order-related)
const PROTECTED_SUBTYPES = [
  'OrderPayment', 'Order', 'OrderCancellation',
  'PaymentReversal', 'PaymentAdjustment', 'OrderAdjustment'
];

const EditTransactionDialog = ({
  open,
  onClose,
  transaction,
  transactionFields = [],
  transactionFieldsSchema = {},
  debtFieldsSchema = {},
  clientFieldsSchema = {},
  orderTransactionFieldsSchema = {},
  fetchTransactions,
  refreshClient,
  userProfile
}) => {
  // Form state
  const [fieldValues, setFieldValues] = useState({});
  // Payment methods state (for Payment transactions)
  const [paymentMethods, setPaymentMethods] = useState([]);

  // UI state
  const [localError, setLocalError] = useState('');
  const [localSuccess, setLocalSuccess] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);
  const errorRef = useRef(null);

  // Memoize configuration values
  const uiConfig = useMemo(() => userProfile?.ui_config || {}, [userProfile]);

  // Check if this is a protected transaction (order-related)
  const isProtectedTransaction = useMemo(() => {
    return transaction && PROTECTED_SUBTYPES.includes(transaction.SubType);
  }, [transaction]);

  // Fields that should NEVER be edited
  const nonEditableFields = useMemo(() =>
    ['TrID', 'Type', 'SubType', 'TotalAfterTR', 'Timestamp', 'Client', 'ClientName', 'ClientBusinessID', 'IsEdited'],
    []
  );

  // For protected transactions, Amount and ActualDate are also non-editable
  const allNonEditableFields = useMemo(() => {
    if (isProtectedTransaction) {
      return [...nonEditableFields, 'Amount', 'ActualDate'];
    }
    return nonEditableFields;
  }, [nonEditableFields, isProtectedTransaction]);

  // Get all editable fields from transaction fields
  const editableFields = useMemo(() =>
    transactionFields.filter(field =>
      !allNonEditableFields.includes(field) &&
      transactionFieldsSchema[field]?.readonly !== true
    ),
    [transactionFields, transactionFieldsSchema, allNonEditableFields]
  );

  // Initialize form values when transaction changes
  useEffect(() => {
    if (transaction) {
      const initialValues = {};

      // Initialize all editable fields including Amount and ActualDate
      [...editableFields, 'Amount', 'ActualDate'].forEach(field => {
        let value = transaction[field];
        if (value === undefined || value === null) {
          value = '';
        } else if (typeof value === 'object' && !(value instanceof Date)) {
          value = JSON.stringify(value);
        }
        initialValues[field] = value;
      });

      setFieldValues(initialValues);

      // Initialize payment methods for Payment transactions
      if (transaction.Type === 'Payment' && transaction.payment_methods?.length > 0) {
        const initialPaymentMethods = transaction.payment_methods.map(pm => ({
          id: pm.PaymentMethodID,
          type: pm.Type,
          amount: pm.Amount?.toString() || '',
          // Check fields
          check_number: pm.check_details?.CheckNumber || '',
          check_bank_name: pm.check_details?.BankName || '',
          check_account_number: pm.check_details?.AccountNumber || '',
          check_due_date: pm.check_details?.DueDate ? new Date(pm.check_details.DueDate) : null,
          check_status: pm.check_details?.Status || 'pending',
          // Card fields
          card_last_four: pm.CardLastFour || '',
          card_type: pm.CardType || '',
          // Transfer fields
          bank_name: pm.BankName || '',
          bank_account_last4: pm.BankAccountLast4 || '',
          transfer_date: pm.TransferDate ? new Date(pm.TransferDate) : null,
          notes: pm.Notes || '',
        }));
        setPaymentMethods(initialPaymentMethods);
      } else {
        setPaymentMethods([]);
      }
    }
  }, [transaction, editableFields]);

  // Scroll to error when it appears
  useEffect(() => {
    if (localError && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [localError]);

  // Helper function to get field label with proper translation
  const getFieldLabel = (fieldName) => {
    if (fieldName === 'Amount') return 'סכום';
    if (fieldName === 'ActualDate') return 'תאריך תנועה';
    if (fieldName === 'Note') return 'הערה';
    if (fieldName === 'Invoice') return 'מספר חשבונית';

    return transactionFieldsSchema[fieldName]?.label ||
           debtFieldsSchema[fieldName]?.label ||
           clientFieldsSchema[fieldName]?.label ||
           orderTransactionFieldsSchema[fieldName]?.label ||
           fieldName;
  };

  // Helper function to format display values
  const formatDisplayValue = (fieldName, value) => {
    if (value === null || value === undefined || value === '') {
      return '–';
    }

    // Type translation
    if (fieldName === 'Type') {
      if (value === 'Payment') return 'תשלום';
      if (value === 'Charge') return 'חיוב';
      if (value === 'Credit') return 'זיכוי';
      return value;
    }

    // SubType translation using schemas
    if (fieldName === 'SubType') {
      if (debtFieldsSchema[value]) {
        return debtFieldsSchema[value].label;
      }
      if (clientFieldsSchema[value]) {
        return clientFieldsSchema[value].label;
      }
      if (orderTransactionFieldsSchema[value]) {
        return orderTransactionFieldsSchema[value].label;
      }
      return value;
    }

    // Date formatting
    if (fieldName === 'Timestamp' || fieldName === 'ActualDate' || fieldName.includes('Date') || fieldName.includes('_at')) {
      const date = new Date(value);
      return date.toLocaleString('he-IL');
    }

    // Amount formatting
    if (fieldName === 'Amount' || fieldName === 'TotalAfterTR') {
      const amount = parseFloat(value) || 0;
      return new Intl.NumberFormat('he-IL', {
        style: 'currency',
        currency: 'ILS',
      }).format(amount);
    }

    return value.toString();
  };

  // Reset form state
  const resetForm = () => {
    setFieldValues({});
    setPaymentMethods([]);
    setLocalError('');
    setLocalSuccess('');
  };

  // Handle form close
  const handleClose = () => {
    resetForm();
    onClose();
  };

  // Update field value
  const handleFieldChange = (field, value) => {
    setFieldValues(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Submit changes
  const handleSubmit = async () => {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    setIsSubmitting(true);
    setLocalError('');
    setLocalSuccess('');

    try {
      // Prepare the payload
      const payload = {
        transaction_id: transaction.TrID,
      };

      // Add amount if editable and changed
      if (!isProtectedTransaction && fieldValues.Amount !== undefined) {
        const newAmount = parseFloat(fieldValues.Amount);
        if (!isNaN(newAmount) && newAmount > 0) {
          payload.amount = newAmount;
        }
      }

      // Add actual_date if editable and set
      if (!isProtectedTransaction && fieldValues.ActualDate) {
        const dateValue = fieldValues.ActualDate instanceof Date
          ? fieldValues.ActualDate
          : new Date(fieldValues.ActualDate);
        if (!isNaN(dateValue.getTime())) {
          payload.actual_date = dateValue.toISOString();
        }
      }

      // Add all other editable field values to payload
      editableFields.forEach(field => {
        if (field === 'Amount' || field === 'ActualDate') return; // Already handled above

        let value = fieldValues[field] || '';

        // Try to parse JSON strings back to objects if they look like JSON
        if (typeof value === 'string' && value.trim().startsWith('{') && value.trim().endsWith('}')) {
          try {
            value = JSON.parse(value);
          } catch (e) {
            // If parsing fails, keep it as string
          }
        }

        if (field === 'Note') {
          payload.note = value;
        } else if (field === 'Invoice') {
          payload.invoice = value;
        } else {
          payload[field] = value;
        }
      });

      // Include payment methods for Payment transactions
      if (transaction.Type === 'Payment' && paymentMethods.length > 0) {
        // Validate payment methods sum equals amount
        const pmTotal = paymentMethods.reduce((sum, pm) => sum + (parseFloat(pm.amount) || 0), 0);
        const transactionAmount = parseFloat(fieldValues.Amount) || parseFloat(transaction.Amount) || 0;
        if (Math.abs(pmTotal - transactionAmount) > 0.01) {
          setLocalError('סכום אמצעי התשלום חייב להיות שווה לסכום התנועה');
          setIsSubmitting(false);
          isSubmittingRef.current = false;
          return;
        }

        payload.payment_methods = paymentMethods.map(pm => ({
          id: pm.id || null,  // null for new payment methods
          type: pm.type,
          amount: parseFloat(pm.amount) || 0,
          // Check fields
          ...(pm.type === 'check' && {
            check_number: pm.check_number,
            check_bank_name: pm.check_bank_name,
            check_account_number: pm.check_account_number,
            check_due_date: pm.check_due_date ? format(pm.check_due_date, 'yyyy-MM-dd') : null,
            check_status: pm.check_status || 'pending',
          }),
          // Card fields
          ...(pm.type === 'credit_card' && {
            card_last_four: pm.card_last_four,
            card_type: pm.card_type,
          }),
          // Transfer fields
          ...(pm.type === 'bank_transfer' && {
            bank_name: pm.bank_name,
            bank_account_last4: pm.bank_account_last4,
            transfer_date: pm.transfer_date ? format(pm.transfer_date, 'yyyy-MM-dd') : null,
          }),
          notes: pm.notes || '',
        }));
      }

      await API.put('/transaction/update/', payload);

      setLocalSuccess('התנועה עודכנה בהצלחה!');

      if (fetchTransactions) {
        fetchTransactions();
      }

      // Refresh client data to update debt display
      if (refreshClient) {
        refreshClient();
      }

      // Close dialog after delay
      setTimeout(() => {
        handleClose();
      }, 1500);
    } catch (err) {
      console.error('שגיאה בעדכון התנועה:', err);
      setLocalError(
        err?.response?.data?.error ||
        err.message ||
        'שגיאה בעת עדכון התנועה'
      );
    } finally {
      setIsSubmitting(false);
      isSubmittingRef.current = false;
    }
  };

  if (!transaction) {
    return null;
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={he}>
      <MobileDialog
        open={open}
        onClose={(event, reason) => {
          if (reason !== 'backdropClick' && !isSubmitting) {
            handleClose();
          }
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
          עריכת תנועה
        </DialogTitle>

        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {localError && (
              <Alert ref={errorRef} severity="error" sx={{ fontWeight: 'bold' }}>{localError}</Alert>
            )}
            {localSuccess && (
              <Alert severity="success" sx={{ fontWeight: 'bold' }}>{localSuccess}</Alert>
            )}

            {/* Display non-editable fields for context */}
            <Card sx={{ backgroundColor: '#f8f8f8', boxShadow: 2 }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#1976d2' }}>
                  פרטי תנועה (לא ניתנים לעריכה)
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                      סוג תנועה
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                      {formatDisplayValue('Type', transaction.Type)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                      {getFieldLabel('SubType')}
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                      {formatDisplayValue('SubType', transaction.SubType)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                      יתרה לאחר תנועה
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                      {formatDisplayValue('TotalAfterTR', transaction.TotalAfterTR)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                      תאריך הקלדה
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                      {formatDisplayValue('Timestamp', transaction.Timestamp)}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>

            <Divider sx={{ my: 1 }} />

            {/* Editable fields section */}
            <Box>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#388e3c' }}>
                שדות הניתנים לעריכה
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {/* Amount field - only editable for non-protected transactions */}
                {!isProtectedTransaction ? (
                  <TextField
                    label={getFieldLabel('Amount')}
                    type="number"
                    value={fieldValues.Amount || ''}
                    onChange={(e) => handleFieldChange('Amount', e.target.value)}
                    onWheel={(e) => e.target.blur()}
                    onFocus={(e) => e.target.select()}
                    fullWidth
                    inputProps={{ min: 0, step: 0.01 }}
                    sx={{
                      '& .MuiInputLabel-root': { fontWeight: 'bold', fontSize: '1.1rem' },
                      '& .MuiInputBase-input': { fontSize: '1.1rem', textAlign: 'center' },
                      direction: 'ltr',
                    }}
                  />
                ) : (
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                      {getFieldLabel('Amount')} (לא ניתן לעריכה - תנועת מערכת)
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                      {formatDisplayValue('Amount', transaction.Amount)}
                    </Typography>
                  </Box>
                )}

                {/* ActualDate field - only editable for non-protected transactions */}
                {!isProtectedTransaction ? (
                  <DatePicker
                    label={getFieldLabel('ActualDate')}
                    value={fieldValues.ActualDate ? (fieldValues.ActualDate instanceof Date ? fieldValues.ActualDate : new Date(fieldValues.ActualDate)) : null}
                    onChange={(newValue) => handleFieldChange('ActualDate', newValue)}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        sx: {
                          '& .MuiInputLabel-root': { fontWeight: 'bold', fontSize: '1.1rem' },
                          '& .MuiInputBase-input': { fontSize: '1.1rem' },
                        }
                      }
                    }}
                  />
                ) : (
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                      {getFieldLabel('ActualDate')} (לא ניתן לעריכה - תנועת מערכת)
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                      {formatDisplayValue('ActualDate', transaction.ActualDate)}
                    </Typography>
                  </Box>
                )}

                {/* All other editable fields */}
                {editableFields.filter(f => f !== 'Amount' && f !== 'ActualDate').map((fieldName) => {
                  const schema = transactionFieldsSchema[fieldName] || {};
                  const fieldType = schema.type === 'number' ? 'number' : 'text';
                  const isMultiline = fieldName === 'Note' || fieldName === 'Notes' ||
                                     fieldName.toLowerCase().includes('note') ||
                                     fieldName.toLowerCase().includes('comment');

                  return (
                    <TextField
                      key={fieldName}
                      label={getFieldLabel(fieldName)}
                      type={fieldType}
                      value={String(fieldValues[fieldName] || '')}
                      onChange={(e) => handleFieldChange(fieldName, e.target.value)}
                      onFocus={(e) => e.target.select()}
                      fullWidth
                      multiline={isMultiline}
                      minRows={isMultiline ? 3 : 1}
                      sx={{
                        '& .MuiInputLabel-root': {
                          fontWeight: 'bold',
                          fontSize: '1.1rem',
                        },
                        '& .MuiInputBase-input': {
                          fontSize: '1.1rem',
                        },
                        direction: fieldType === 'number' ? 'ltr' : 'rtl',
                        '& input': {
                          textAlign: fieldType === 'number' ? 'center' : 'right',
                          '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
                            WebkitAppearance: 'none',
                            margin: 0,
                          },
                          '-moz-appearance': 'textfield',
                        },
                      }}
                      InputProps={{
                        style: {
                          fontWeight: 'bold',
                          direction: fieldName === 'Invoice' ? 'ltr' : 'rtl'
                        }
                      }}
                    />
                  );
                })}

                {editableFields.length === 0 && isProtectedTransaction && (
                  <Alert severity="info" sx={{ fontWeight: 'bold' }}>
                    תנועה זו הינה תנועת מערכת ולא ניתנת לעריכה
                  </Alert>
                )}
              </Box>
            </Box>

            {/* Payment Methods Section - Only for Payment transactions */}
            {transaction.Type === 'Payment' && !isProtectedTransaction && (
              <>
                <Divider sx={{ my: 2 }} />
                <Box>
                  <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold', color: '#1976d2' }}>
                    אמצעי תשלום
                  </Typography>
                  <PaymentMethodRows
                    paymentMethods={paymentMethods}
                    setPaymentMethods={setPaymentMethods}
                    totalAmount={parseFloat(fieldValues.Amount) || parseFloat(transaction.Amount) || 0}
                    compact={true}
                  />
                </Box>
              </>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={handleClose}
            color="inherit"
            size="large"
            sx={{ fontSize: '1.1rem', fontWeight: 'bold' }}
            disabled={isSubmitting}
          >
            ביטול
          </Button>

          <Button
            variant="contained"
            color="primary"
            size="large"
            sx={{ fontSize: '1.1rem', fontWeight: 'bold', minWidth: 120 }}
            onClick={handleSubmit}
            disabled={isSubmitting || localSuccess}
          >
            {isSubmitting ? (
              <CircularProgress size={24} />
            ) : (
              'שמור שינויים'
            )}
          </Button>
        </DialogActions>
      </MobileDialog>
    </LocalizationProvider>
  );
};

export default EditTransactionDialog;
